<template>
  <div
    id="store-teaser-list"
    class="store-teaser-list"
  >
    <div
      class="store-teaser-list-wrapper"
      :class="{
        'store-teaser-list-wrapper--empty': storesToShow.length <= 0,
      }"
    >
      <template v-if="storesToShow.length > 0">
        <fn-collapsible-panel
          :open-to-top="openToTop"
          :default-opened="defaultOpened"
        >
          <template #header>
            {{ $t('storeListing.search.storesNearby', { storesNumber: stores.length }) }}
          </template>

          <div class="stl-items">
            <fn-store-teaser
              v-for="(store, index) in storesToShow"
              :id="'store-teaser-' + index"
              :key="store.name"
              class="stl-item"
              :store="store"
              :favorite-store="storeFavorite(store)"
              @show-details="$emit('showDetails', store)"
              @calc-route="$emit('calcRoute', store)"
            />
          </div>
        </fn-collapsible-panel>
      </template>
      <template v-else>
        <client-only>
          <fn-store-teaser
            v-if="favoriteStore"
            id="store-teaser-favorite"
            :store="favoriteStore"
            favorite-store
            @show-details="$emit('showDetails', favoriteStore)"
            @calc-route="$emit('calcRoute', favoriteStore)"
          />
        </client-only>
        <div class="stl-advantages">
          <fn-headline
            type="h2"
            size="h5"
          >
            {{ $t('storeListing.advantages.title') }}
          </fn-headline>
          <fn-list
            class="advantages-list"
            unstyled
            icons
          >
            <li
              v-for="(listItem, index) of $tm('storeListing.advantages.list')"
              :key="index"
              class="al-item"
            >
              <fn-icon
                icon="check"
                color="green-dark"
                size="xxs"
                role="presentation"
              />
              <span v-html="$rt(listItem)" />
            </li>
          </fn-list>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
export default defineComponent({
  props: {
    stores: {
      type: Array,
      default: () => [],
    },
    openToTop: {
      type: Boolean,
    },
    defaultOpened: {
      type: Boolean,
    },
  },
  emits: ['calcRoute', 'showDetails'],
  computed: {
    ...mapState(useUserStore, {
      favoriteStore: 'getUserFavoriteStore',
    }),
    storesToShow() {
      const storeIndex = this.stores.findIndex((store) => this.storeFavorite(store));

      if (storeIndex > 0) {
        const newStoresList = [...this.stores];

        const favoriteStore = newStoresList.splice(storeIndex, 1);

        return [...favoriteStore, ...newStoresList];
      }

      return this.stores?.filter((store) => (store as any).active) ?? [];
    },
  },
  methods: {
    storeFavorite(store: any) {
      if (!this.favoriteStore) return false;

      return store.name === this.favoriteStore.name;
    },
  },
});
</script>

<style lang="scss" scoped>
.store-teaser-list {
  @include bp(m) {
    display: flex;
    align-items: flex-end;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: auto;
    z-index: 1;
    width: 375px;
    height: calc(75vh);
    pointer-events: none;

    .store-teaser-list-wrapper {
      pointer-events: all;
      width: 100%;
      max-height: 100%;
      display: flex;

      &.store-teaser-list-wrapper--empty {
        flex-direction: column;
        overflow-y: auto;
      }
    }
  }

  .stl-item {
    border-bottom: 1px solid palette(grey, light);

    &:last-child {
      border-bottom: 0;
    }
  }

  .stl-advantages {
    padding: $space $pad;
    background-color: $color-general-background-neutral-default;
    border-top: 1px solid palette(grey, lighter);

    .advantages-list {
      font-size: map-get($font-sizes-headings, 'xxs');

      .al-item {
        margin-bottom: math.div($pad, 2);

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}
</style>
